import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
    imeiFailure,
    imeiSuccess,
    portFailure,
    portSuccess,
    portResultFailure,
    portResultSuccess,
    getCCFailure,
    getCCSuccess,
    updateCCAutoPayFailure,
    updateCCAutoPaySuccess,
    removeCCFailure,
    removeCCSuccess,
    ccHoldFailure,
    ccHoldSuccess,
    checkIccFailure,
    checkIccSuccess,
    updateCCExpiryFailure,
    updateCCExpirySuccess,
    addCardFailure,
    addCardSuccess,
    activationFailure,
    activationSuccess,
    activationStatusFailure,
    activationStatusSuccess,
    dataUsageFailure,
    dataUsageSuccess,
    reachCreditFailure,
    reachCreditSuccess,
    getAppDataSuccess,
    getAppDataFailure,
    overAllFailure,
    overAllSuccess,
    monthInAdvanceSuccess,
    monthInAdvanceFailure,
    monthInAdvancePostSuccess,
    monthInAdvancePostFailure,
    resendESimKitSuccess,
    currentDataUsageSuccess,
    currentDataUsageFailure,
    resendESimKitFailure,
} from './actions';
import * as types from './actionTypes';
import apiJunction from '../../utils/api';
import { domainName, domainMap, version } from '../../config';
import ApiEndPoints from '../../utils/ApiEndPoints';

export interface ResponseGenerator {
    config?: any;
    data?: any;
    headers?: any;
    request?: any;
    status?: number;
    statusText?: string;
}

// get city.state by zipcode
function* imei(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `${ApiEndPoints.imei}/${action.payload.countryCode}/${action.payload.imei}`,
            other1: action.payload.other1,
        });
        yield put(
            imeiSuccess({
                imei: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            imeiFailure({
                errorImei: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// validate port
function* port(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'post',
            url: `${ApiEndPoints?.port}/${action.payload.countryCode}/${action.payload.number}`,
            body: {},
            other: action.payload.isPrimary,
        });
        yield put(
            portSuccess({
                port: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            portFailure({
                errorPort: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// validate port
function* portResult(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `${ApiEndPoints?.portResult}/${action.payload.data}`,
        });
        yield put(
            portResultSuccess({
                portResult: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            portResultFailure({
                errorPortResult: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// get cc details
function* getCC(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url:
                domainName === domainMap.T2
                    ? `/custsvc/${version}/customer/cc/${action.payload.customerId}`
                    : `/custsvc/${version}/customer/cc/${action.payload.customerId}`,
        });
        yield put(
            getCCSuccess({
                getCC: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            getCCFailure({
                errorGetCC: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// update cc autopay
function* updateCCAutoPay(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'put',
            url:
                domainName === domainMap.T2
                    ? `/custsvc/${version}/customer/cc/autopay/${action.payload.uuid}`
                    : `/custsvc/${version}/customer/cc/autopay/${action.payload.uuid}`,
            body: {},
        });
        yield put(
            updateCCAutoPaySuccess({
                updateCCAutoPay: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            updateCCAutoPayFailure({
                errorUpdateCCAutoPay: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// remove cc
function* removeCC(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'delete',
            url:
                domainName === domainMap.T2
                    ? `/custsvc/${version}/customer/cc/delete/${action.payload.uuid}`
                    : `/custsvc/${version}/customer/cc/delete/${action.payload.uuid}`,
        });
        yield put(
            removeCCSuccess({
                removeCC: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            removeCCFailure({
                errorRemoveCC: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// cc hold
function* ccHold(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'post',
            url: `/custsvc/${version}/customer/cc/hold/${action.payload.customerId}`,
        });
        yield put(
            ccHoldSuccess({
                ccHold: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            ccHoldFailure({
                errorCCHold: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// check ICC
function* checkIcc(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `/provsvc/${version}/icc/validate/${action.payload.customerId}/${action.payload.iccid}`,
        });
        yield put(
            checkIccSuccess({
                checkIcc: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            checkIccFailure({
                errorCheckIcc: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// update cc expiry
function* updateCCExpiry(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'put',
            url:
                domainName === domainMap.T2
                    ? `/custsvc/${version}/cc/update/${action.payload.uuid}/expiry/${action.payload.expiry}`
                    : `/custsvc/${version}/cc/update/${action.payload.uuid}/expiry/${action.payload.expiry}`,
        });
        yield put(
            updateCCExpirySuccess({
                updateCCExpiry: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            updateCCExpiryFailure({
                errorUpdateCCExpiry: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// add card
function* addCard(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'post',
            url: `https://7ytxbyv8s4.execute-api.us-east-1.amazonaws.com/dev/v0/page/card/generate`,
            body: action.payload,
            contentType: 'text/plain',
        });
        yield put(
            addCardSuccess({
                addCard: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            addCardFailure({
                errorAddCard: e.response && e.response.data,
            }),
        );
    }
}

// activation
function* activation(action: any) {
    // console.log("action payload - ", action);
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'post',
            url: domainName === domainMap.T2 ? ApiEndPoints?.activation : ApiEndPoints?.activation,
            body: action.payload,
        });
        yield put(
            activationSuccess({
                activation: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            activationFailure({
                errorActivation: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// activation status
function* activationStatus(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url:
                domainName === domainMap.T2
                    ? `${ApiEndPoints?.activationStatus}/${action.payload.customerId}`
                    : `${ApiEndPoints?.activationStatus}/${action.payload.customerId}`,
        });
        yield put(
            activationStatusSuccess({
                activationStatus: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            activationStatusFailure({
                errorActivationStatus: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// data usage
function* dataUsage(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url:
                domainName === domainMap.T2
                    ? `${ApiEndPoints?.dataUsage}/${action.payload.customerId}?startDate=${action.payload.startDate}&endDate=${action.payload.endDate}`
                    : `${ApiEndPoints?.dataUsage}/${action.payload.customerId}`,
        });
        yield put(
            dataUsageSuccess({
                dataUsage: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            dataUsageFailure({
                errorDataUsage: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

function* currentDataUsage(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `${ApiEndPoints?.currentDataUsage}/${action.payload.customerId}`,
        });
        yield put(
            currentDataUsageSuccess({
                currentDataUsage: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            currentDataUsageFailure({
                errorCurrentDataUsage: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

// reach credit
function* reachCredit() {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            // url: `/config/id/PROV_CONFIG/keys/${action.payload.keys}`,
            url: `/confsvc/${version}/config/keys/`,
        });
        yield put(
            reachCreditSuccess({
                reachCredit: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            reachCreditFailure({
                errorReachCredit: e.response && e.response.data,
            }),
        );
    }
}
// get App Data
function* getAppData() {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `${ApiEndPoints?.appData}`,
        });
        yield put(
            getAppDataSuccess({
                getAppData: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            getAppDataFailure({
                errorGetAppData: e.response && e.response.data,
            }),
        );
    }
}

// over all
function* overAll() {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `${process.env.REACT_APP_CSR_ADDRESS}/dataPool/analytics/overall`,
        });
        yield put(
            overAllSuccess({
                overAll: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            overAllFailure({
                errorOverAll: e.response && e.response.data,
            }),
        );
    }
}

//MONTH_IN_ADVANCE
function* monthInAdvanceSaga(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `billsvc/v0/month/in/advance/amount/${action.payload.customerId}`,
            // body: action.payload,
        });
        yield put(
            monthInAdvanceSuccess({
                monthInAdvance: response.data.data,
            }),
        );
    } catch (e: any) {
        yield put(
            monthInAdvanceFailure({
                errorMonthInAdvance: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

//monthinadvance post

function* monthInAdvancePostSaga(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'post',
            url: `billsvc/v0/month/in/advance/pay/${action.payload.customerId}`,
            body: {},
        });
        yield put(
            monthInAdvancePostSuccess({
                monthInAdvancePost: response.data,
            }),
        );
    } catch (e: any) {
        yield put(
            monthInAdvancePostFailure({
                errorMonthInAdvancePost: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

function* resendEsimKitSaga(action: any) {
    try {
        const response: ResponseGenerator = yield call(apiJunction.makeRequest, {
            method: 'get',
            url: `provsvc/v0/resend/qr/${action.payload.customerId}`,
        });
        yield put(
            resendESimKitSuccess({
                resendStatus: response.data.data,
            }),
        );
    } catch (e: any) {
        yield put(
            resendESimKitFailure({
                errorResendStatus: e.response ? e.response.data : { status: 'FAILURE', message: 'error' },
            }),
        );
    }
}

function* ActivationSaga() {
    yield all([
        takeLatest(types.IMEI, imei),
        takeLatest(types.PORT, port),
        takeLatest(types.PORT_RESULT, portResult),
        takeLatest(types.GET_CC, getCC),
        takeLatest(types.UPDATE_CC_AUTOPAY, updateCCAutoPay),
        takeLatest(types.REMOVE_CC, removeCC),
        takeLatest(types.CC_HOLD, ccHold),
        takeLatest(types.CHECK_ICC, checkIcc),
        takeLatest(types.UPDATE_CC_EXPIRY, updateCCExpiry),
        takeLatest(types.ADD_CARD, addCard),
        takeLatest(types.ACTIVATION, activation),
        takeLatest(types.ACTIVATION_STATUS, activationStatus),
        takeLatest(types.DATA_USAGE, dataUsage),
        takeLatest(types.REACH_CREDIT, reachCredit),
        takeLatest(types.GET_APP_DATA, getAppData),
        takeLatest(types.OVER_ALL, overAll),
        takeLatest(types.MONTH_IN_ADVANCE, monthInAdvanceSaga),
        takeLatest(types.MONTH_IN_ADVANCE_POST, monthInAdvancePostSaga),
        takeLatest(types.RESEND_ESIM_KIT, resendEsimKitSaga),
        takeLatest(types.CURRENT_DATA_USAGE, currentDataUsage),
    ]);
}

export default ActivationSaga;
